import React, { useEffect } from "react";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";

import { FiChevronDown } from "react-icons/fi";
import { IoIosArrowForward } from "react-icons/io";

import { useDispatch, useSelector } from "react-redux";
import { submitReferData } from "../redux/slice/referralSlice";
import { allEarningList } from "../redux/slice/passbookSlice";
import "./dashboardStyle.css";
import "./dashboard.css";
import { NavLink } from "react-router-dom";
import DashboardBanner from "./components/DashboardBanner";
import DashboardTabbar from "./components/DashboardTabbar";

const DashbordHome = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    window.scrollTo(0, 0);
    const storedReferralCode = sessionStorage.getItem("referrerCode");
    if (storedReferralCode) {
      dispatch(submitReferData({ referralCode: storedReferralCode }));
    }
    dispatch(allEarningList());
  }, [dispatch]);

  const { allEarningListData = {} } = useSelector((state) => state.passbook);

  const earnings = Array.isArray(allEarningListData.data)
    ? allEarningListData.data
    : [];

  // Revenue chart: group by month and sum amount
  const revenueData = [
    { name: "Jan", uv: 0, pv: 0 },
    { name: "Feb", uv: 0, pv: 0 },
    { name: "Mar", uv: 0, pv: 0 },
    { name: "Apr", uv: 0, pv: 0 },
    { name: "May", uv: 0, pv: 0 },
    { name: "Jun", uv: 0, pv: 0 },
    { name: "Jul", uv: 0, pv: 0 },
    { name: "Aug", uv: 0, pv: 0 },
    { name: "Sep", uv: 0, pv: 0 },
    { name: "Oct", uv: 0, pv: 0 },
    { name: "Nov", uv: 0, pv: 0 },
    { name: "Dec", uv: 0, pv: 0 },
  ];
  earnings.forEach((e) => {
    const date = new Date(e.createdAt);
    const month = date.getMonth();
    if (revenueData[month]) {
      revenueData[month].uv += e.amount;
      revenueData[month].pv += e.amount;
    }
  });

  // Top Referral Earnings
  const aggregateByKey = (items, keyFn) => {
    const map = {};
    items.forEach((e) => {
      const key =
        keyFn(e) ||
        e.user_email ||
        e.referredEmail ||
        e.referredName ||
        "unknown";
      if (!map[key]) {
        map[key] = {
          sku: key,
          name: e.description || "",
          qty: 0,
          revenue: 0,
          profit: 0,
        };
      }
      map[key].qty += 1;
      map[key].revenue += Number(e.amount) || 0;
      map[key].profit += Number(e.amount) || 0;
      if (!map[key].name && e.description) map[key].name = e.description;
    });
    return Object.values(map)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  };

  const topProducts = aggregateByKey(
    earnings.filter(
      (e) => e.type === "referralCommission" || e.type === "referralReward",
    ),
    (e) => e.referredName || e.referredEmail || e.user_email,
  );

  const potentialProducts = aggregateByKey(
    earnings.filter((e) => e.type === "profile-surve"),
    (e) => e.user_email || e.referredEmail || e.referredName,
  );

  const feedbackProducts = aggregateByKey(
    earnings.filter((e) => e.type === "feedback-survey"),
    (e) => e.user_email || e.referredEmail || e.referredName,
  );

  // Monthly earnings for bar chart
  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const monthlyMap = {};
  earnings.forEach((e) => {
    const date = new Date(e.createdAt);
    const month = monthNames[date.getMonth()];
    if (!monthlyMap[month])
      monthlyMap[month] = { name: month, Online: 0, Telephonic: 0, In_depth: 0 };
    if (e.type === "referralCommission" || e.type === "referralReward")
      monthlyMap[month].Online += e.amount;
    else if (e.type === "profile-survey")
      monthlyMap[month].In_depth += e.amount;
    else if (e.type === "feedback-survey")
      monthlyMap[month].Telephonic += e.amount;
  });
  const data = monthNames.map(
    (m) => monthlyMap[m] || { name: m, Online: 0, Telephonic: 0, In_depth: 0 },
  );

  // Wallet Statement: latest 5 earnings
  const transactions = earnings.slice(0, 5).map((e) => ({
    name: e.user_email,
    date: new Date(e.createdAt).toLocaleDateString(),
    amount: `${e.currency} ${e.amount}`,
    status: e.type,
  }));

  return (
    <div>
      <DashboardBanner />
      <div className="container dashboard-page mt-4 min-h">
        <DashboardTabbar />
        <div className="dashboard-content-wrapper">
          <div className="row g-4">
            <div className="col-lg-8">
              <div className="row g-4">
                {/* Revenue Growth Card */}
                <div className="col-12">
                  <div className="row g-0">
                    <div className="col-md-8">
                      <div className="dashboard-card h-100">
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <h5 className="card-title mb-0">My Earning</h5>
                          <div className="d-flex align-items-center">
                            <span className="me-2 text-primary">● This Year</span>
                            <FiChevronDown />
                          </div>
                        </div>
                        <div className="card-body">
                          <div className="chart-container" style={{ height: "250px", width: "100%" }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart
                                data={revenueData}
                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f19633" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} />
                                <YAxis axisLine={false} tickLine={false} tickFormatter={(value) => `${value}S`} />
                                <Tooltip />
                                <Line type="monotone" dataKey="uv" stroke="#7d2b6b" strokeWidth={3} dot={false} name="Actual" />
                                <Line type="monotone" dataKey="pv" stroke="#dc4f60" strokeWidth={3} dot={false} name="Expected" />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="dashboard-card h-100">
                        <div className="card-body">
                          <h5 className="card-title mb-3">Earning Summary</h5>
                          <div className="summary-item mb-2">
                            <p className="mb-0 text-muted">Total Earning</p>
                            <h4 className="mb-0 fw-bold">
                              {earnings.reduce((sum, e) => sum + e.amount, 0)}
                            </h4>
                          </div>
                          <hr />
                          <div className="summary-item mb-2">
                            <p className="mb-0 text-muted">Opportunities Missed</p>
                            <h4 className="mb-0 fw-bold">0</h4>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="row g-4">
                <div className="col-12">
                  <div className="dashboard-card boost-sale-card text-white overflow-hidden">
                    <div className="card-body position-relative">
                      <div className="background-design"></div>
                      <h3 className="card-title mb-2 z-index-1">Boost Your Earnings</h3>
                      <p className="card-text mb-3 z-index-1">
                        Share your referral link and earn up to 30% more. Start referring now!
                      </p>
                      <NavLink to="/refer" className="btn btn-upgrade z-index-1">
                        Refer Now
                      </NavLink>
                    </div>
                  </div>
                </div>
                <div className="col-md-12 col-sm-6">
                  <div className="add-project-card position-relative p-3 rounded-3 shadow-sm d-flex">
                    <div className="card-content">
                      <h5 className="mb-2">Share your Feedback and earn more</h5>
                      <NavLink to="/feedback" className="btn btn-primary btn-add-project">
                        Feedback
                      </NavLink>
                    </div>
                    <img className="add-project-img" src="./images/dashboard-banner.gif" alt="" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-4 mt-3">
            <div className="col-lg-4">
              <div className="dashboard-card h-100">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">Top Referral Earnings</h5>
                  <NavLink to="/refer" className="view-all-link">
                    View All <IoIosArrowForward />
                  </NavLink>
                </div>
                <div className="card-body">
                  {topProducts.map((product, index) => (
                    <div key={index} className="product-item d-flex align-items-center py-2">
                      <div className="product-details flex-grow-1">
                        <h6 className="mb-0">{product.sku}</h6>
                      </div>
                      <div className="product-stats text-end">
                        <p className="mb-0 text-primary">{product.revenue}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="dashboard-card h-100">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">Potential Referral Earnings</h5>
                </div>
                <div className="card-body">
                  {potentialProducts.map((product, index) => (
                    <div key={index} className="product-item d-flex align-items-center py-2">
                      <div className="product-details flex-grow-1">
                        <h6 className="mb-0">{product.sku}</h6>
                        <p className="text-muted mb-0 small" title={product.name}>
                          {product.name && product.name.length > 30
                            ? product.name.slice(0, 30) + "..."
                            : product.name}
                        </p>
                        <p className="mb-0 small text-primary">{product.revenue}</p>
                      </div>
                      <div className="product-stats text-end">
                        <p className="mb-0 small">QTY : {product.qty}</p>
                        <p className="mb-0 small">Profit : {product.profit}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="dashboard-card h-100">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">Feedback Earning</h5>
                  <NavLink to="/feedback" className="view-all-link">
                    View All <IoIosArrowForward />
                  </NavLink>
                </div>
                <div className="card-body">
                  {feedbackProducts.map((product, index) => (
                    <div key={index} className="product-item d-flex align-items-center py-2">
                      <div className="product-details flex-grow-1">
                        <p className="mb-0 small" title={product.name}>
                          {product.name && product.name.length > 30
                            ? product.name.slice(0, 20) + "..."
                            : product.name}
                        </p>
                      </div>
                      <div className="product-stats text-end">
                        <p className="mb-0 small text-primary">{product.revenue}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="row g-4 mt-3">
            <div className="col-lg-4">
              <div className="dashboard-card h-100">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">Referral's Monthly Earning</h5>
                </div>
                <div className="card-body">
                  <div className="chart-container" style={{ height: "400px" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data} layout="vertical" margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                        <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} width={40} />
                        <XAxis type="number" axisLine={false} tickLine={false} hide />
                        <Tooltip />
                        <Bar dataKey="Online" stackId="a" fill="#b12767" barSize={10} />
                        <Bar dataKey="In_depth" stackId="a" fill="#e8a1c2" barSize={20} />
                        <Bar dataKey="Telephonic" stackId="a" fill="#cf6094" barSize={20} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="dashboard-card h-100">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">Wallet Statement</h5>
                  <NavLink to="/earning" className="view-all-link">
                    View All <IoIosArrowForward />
                  </NavLink>
                </div>
                <div className="card-body table-responsive">
                  <table className="table latest-transaction-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((transaction, index) => (
                        <tr key={index}>
                          <td className="small">{transaction.date}</td>
                          <td className="small">{transaction.amount}</td>
                          <td className="small">
                            <span className={`status-text ${transaction.status.toLowerCase()}`}>
                              {transaction.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="dashboard-card h-100">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0" title="Competitor market share">Ad</h5>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashbordHome;
